import { Button } from "primereact/button"
import { Card } from "primereact/card"
import { useCallback, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useParams } from "react-router-dom"
import ProgressSpinnerDialog from "../../../../constants/helpers/ProgressSpinnerDialog"
import { RESUME_KEY } from "../../../../constants/localStorageKeys"

import { InterviewStatusEnum } from "../Chatbot/interface"
import { AppDispatch } from "../../../../store"
import { candidateRetrieveInterview } from "./CandidateInterviewAction"
import Webcam from "react-webcam"
import { FaceMesh } from '@mediapipe/face_mesh';
import { CameraOptions, useFaceDetection } from 'react-use-face-detection';
import FaceDetection from '@mediapipe/face_detection';
import { Camera } from '@mediapipe/camera_utils';
import { Message } from "primereact/message"

interface InterviewProps {
}

const BeginInterview = (props: InterviewProps) => {
  const {} = props
  const { interviewId } = useParams<{ interviewId: string }>()
  const history = useHistory()
  
  const dispatch = useDispatch<AppDispatch>()
  const { interview } = useSelector((state: any) => state.candidateInterview)

  const [loadingMessage, setLoadingMessage] = useState("")
  const resumeToken = localStorage.getItem(RESUME_KEY)

  // const { webcamRef, isLoading, detected, facesDetected } = useFaceDetection({
  //   faceDetectionOptions: {
  //     model: 'short',
  //   },
  //   faceDetection: new FaceDetection.FaceDetection({
  //     locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/face_detection/${file}`,
  //   }),
  //   camera: ({ mediaSrc, onFrame }: CameraOptions) =>
  //     new Camera(mediaSrc, {
  //       onFrame,
  //       width: 300,
  //       height: 300,
  //     })
  // });

  /*
  * State and functions to manage webcam and microphone permissions.
  */
  const webcamRef = useRef<any>(null);
  const [permissions, setPermissions] = useState({
    camera: 'prompt',
    microphone: 'prompt',
  });
  const isAccessGranted = permissions.camera === 'granted' && permissions.microphone === 'granted';
  const videoConstraints = {
    aspectRatio: 1,
    facingMode: "user",
    video: {
      advanced: {
        width: 800,
        height: 800
      }
    }
  };
  const checkPermissions = useCallback(async () => {
    try {
      // Use Promise.all to check both permissions concurrently
      const [cameraStatus, microphoneStatus] = await Promise.all([
        navigator.permissions.query({ name: 'camera' }),
        navigator.permissions.query({ name: 'microphone' }),
      ]);

      setPermissions({
        camera: cameraStatus.state,
        microphone: microphoneStatus.state,
      });

      // Set up onchange listeners to "constantly monitor"
      cameraStatus.onchange = () => setPermissions(prev => ({ ...prev, camera: cameraStatus.state }));
      microphoneStatus.onchange = () => setPermissions(prev => ({ ...prev, microphone: microphoneStatus.state }));

    } catch (err) {
      // This can happen if the browser doesn't support the Permissions API
      console.error("Error checking permissions:", err);
     }
  }, []);

  /*
  * States and functions to detect face in video stream
  */
  const [faceDetected, setFaceDetected] = useState<boolean>(false);
  const faceMeshRef = useRef<FaceMesh | null>(null);
  const cameraRef = useRef<Camera | null>(null);
  const onFaceMeshResult = (results: any) => {
    if (results.multiFaceLandmarks && results.multiFaceLandmarks.length > 0) {
      setFaceDetected(true);
    } else {
      setFaceDetected(false);
    }
  }
  const initFaceMesh = useCallback(() => {
    if (faceMeshRef.current) {
      console.log("FaceMesh already initialized.");
      return;
    }

    console.log("Initializing FaceMesh...");
    faceMeshRef.current = new FaceMesh({
      locateFile: (file) => {
        return `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`;
      }
    });
    // faceMeshRef.current.initialize()
    faceMeshRef.current.setOptions({
      maxNumFaces: 1,
      refineLandmarks: false, // Set to true for more detailed landmarks if needed
      minDetectionConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    faceMeshRef.current.onResults(onFaceMeshResult);

    // Initialize the Camera utility
    // This connects the webcam stream to the FaceMesh model for processing.
    cameraRef.current = new Camera(webcamRef.current.video, {
      onFrame: async () => {
        if (faceMeshRef.current !== null && webcamRef.current?.video) {
          await faceMeshRef.current.send({ image: webcamRef.current.video });
        }
      },
      width: 300,
      height: 300,
    });
    cameraRef.current.start();
  }, []);

  useEffect(() => {
    if (isAccessGranted) {
      // // Initialize FaceMesh when webcam is ready
      // webcamRef.current?.video.addEventListener('loadeddata', (e: any) => {
      //   console.log("Webcam video loaded, checking for face detection...", e);
      //   initFaceMesh()
      // })
    }
  }, [permissions]);

  //Initial effect
  useEffect(() => {
    setLoadingMessage("Preparing your interview...")

    if (interviewId)
      dispatch(candidateRetrieveInterview(interviewId)).unwrap()
      .catch(() => {
        // if interview not found, redirect out!
        console.log("Failed to validate interview, please check resume token and interview id.")
        history.push("/error")
      })

    // Check permissions for webcam and microphone
    checkPermissions()

    return () => {
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
      if (faceMeshRef.current) {
        faceMeshRef.current.close();
        faceMeshRef.current = null;
      }
    }
  }, [])

  const beginInterview = () => {
    history.push("/interview/" + interviewId + "/session")
  }

  const onMedia = () => {
    initFaceMesh()
  }

  const title = "Hi " + interview?.intervieweeName
  const subtitle = (interview?.status === InterviewStatusEnum.ACTIVE) ?
    "You are interviewing for the " + interview?.application?.job?.title + " role."
    :
    "You have either already completed the interview for the " + interview?.job?.title + " role, or the interview has already expired."

  return (
    <div className="flex justify-content-center align-items-center">
      <div>
        <ProgressSpinnerDialog text={loadingMessage} visible={interview === undefined}></ProgressSpinnerDialog>
      </div>
      <div className="mt-5" style={{ maxWidth: "800px" }}>
        <Card className="flex justify-content-center" title={title} subTitle={subtitle}>
          {(interview?.status === InterviewStatusEnum.ACTIVE) ?
            <div className="grid">
              <div className="col-12 md:col text-center">
                {isAccessGranted ?
                  <>
                    {!faceDetected &&
                      <Message text="Please ensure that you're visible." className="mb-3" />}
                    <Webcam
                      audio={true}
                      ref={webcamRef}
                      videoConstraints={videoConstraints}
                      width={300}
                      height={300}
                      onUserMedia={onMedia}
                    />
                  </> :
                  <div className="text-red-500">
                    Please grant access to your camera and microphone to proceed with the interview.
                  </div>
                }
              </div>
              <div className="col-12 md:col flex flex-column justify-content-center">
                <div>
                  <p className="mt-0">This virtual interview will take about 5 minutes to complete.</p>
                  <p>Please click on the button below to begin the interview session with our virtual interviewer.</p>
                  <p>You may copy the URL in your address bar to attend this interview at a more convenient time, but note that this interview will expire in 1 day.</p>
                </div>
                <Button label="Start Interview" onClick={beginInterview} disabled={!isAccessGranted || !faceDetected}></Button>
              </div>
            </div>
            :
            <div>If you wish to re-attempt the interview, please contact the company administrator.</div>
          }
        </Card>
      </div>
    </div>
  )
}

export default BeginInterview